import { useEffect, useMemo, useState } from "react";
import {
  AddButton, Empty, ErrorNote, Pager, pageSlice,
} from "../../components/ui";
import { formatMoney } from "../../lib/money";
import { listCharges, STATUS_LABEL, type ChargeRow } from "./charges";

const STATUS_CLASS: Record<string, string> = {
  open: "hold", paid: "available", refunded: "recon", voided: "sold",
};

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US",
    { month: "short", day: "numeric", year: "2-digit" });

type SortKey = "date" | "charge_no" | "customer" | "amount" | "status";

/**
 * Payments taken outside a deal.
 *
 * Kept separate from Reports on purpose. Reports is about vehicles sold —
 * gross per unit, days on the lot — and folding deposits and fees into those
 * figures would quietly change what every one of them means. The totals that
 * belong to this money are the ones at the top of this page.
 */
export default function PaymentList({ onAdd, onOpen }: {
  onAdd: () => void;
  onOpen: (id: string) => void;
}) {
  const [rows, setRows] = useState<ChargeRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState<SortKey>("date");
  const [desc, setDesc] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    listCharges()
      .then(setRows)
      .catch((e) => setErr(e instanceof Error ? e.message : "Could not load payments."));
  }, []);

  useEffect(() => { setPage(0); }, [q, status]);

  const shown = useMemo(() => {
    if (!rows) return [];
    const n = q.trim().toLowerCase();
    let out = rows.filter((c) => {
      if (status && c.status !== status) return false;
      if (!n) return true;
      return [
        c.charge_no, c.customer_name, c.description ?? "",
        c.vehicle ?? "", c.stock_no ?? "", c.card_last4 ?? "",
      ].some((f) => f.toLowerCase().includes(n));
    });

    const val = (c: ChargeRow) => ({
      date: c.created_at,
      charge_no: c.charge_no,
      customer: c.customer_name,
      amount: c.amount_cents,
      status: c.status,
    })[sort];

    out = [...out].sort((a, b) => {
      const x = val(a), y = val(b);
      const d = typeof x === "number" && typeof y === "number"
        ? x - y : String(x).localeCompare(String(y));
      return desc ? -d : d;
    });
    return out;
  }, [rows, q, status, sort, desc]);

  if (err) return <ErrorNote>{err}</ErrorNote>;
  if (!rows) return <p className="hint">Loading</p>;

  const paged = pageSlice(shown, page);
  const unpaid = rows.filter((c) => c.status === "open");
  const owed = unpaid.reduce((n, c) => n + c.amount_cents, 0);

  /* This month by the money's own date — when it was collected, not when
     somebody typed the charge in. */
  const monthStart = new Date();
  monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
  const thisMonth = rows
    .filter((c) => c.status === "paid" && c.paid_at
                   && new Date(c.paid_at) >= monthStart)
    .reduce((n, c) => n + c.amount_cents, 0);

  const th = (key: SortKey, label: string, align = "") => (
    <th className={`${align} ${sort === key ? "sorted" : ""}`}
        onClick={() => { sort === key ? setDesc(!desc) : (setSort(key), setDesc(true)); }}>
      {label}
      {sort === key && <span className="arrow">{desc ? "▾" : "▴"}</span>}
    </th>
  );

  return (
    <>
      <div className="pagehead">
        <div>
          <h2>Payments</h2>
          <p className="pagehead-sub">
            {formatMoney(thisMonth)} collected this month
            {unpaid.length > 0
              ? ` · ${unpaid.length} unpaid (${formatMoney(owed)})`
              : ""}
          </p>
        </div>
        <AddButton label="Take a payment" onClick={onAdd} />
      </div>

      {rows.length === 0 ? (
        <Empty>
          No payments yet.<br />
          This is for money that is not settling a deal — a deposit, a fee, a
          repair. Payments on a deal go on the deal.
        </Empty>
      ) : (
        <>
          <div className="filters">
            <input className="search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search reference, customer, description or vehicle" />
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All payments</option>
              <option value="open">Unpaid</option>
              <option value="paid">Paid</option>
              <option value="refunded">Refunded</option>
              <option value="voided">Cancelled</option>
            </select>
          </div>

          {(q || status) && (
            <p className="resultnote">
              {shown.length} of {rows.length} —{" "}
              <button className="linkbtn" onClick={() => { setQ(""); setStatus(""); }}>
                clear
              </button>
            </p>
          )}

          {shown.length === 0 ? <Empty>Nothing matches that.</Empty> : (
            <>
              <div className="tablewrap">
                <table className="dealtable">
                  <thead>
                    <tr>
                      {th("date", "Date")}
                      {th("charge_no", "Ref")}
                      {th("customer", "Customer")}
                      <th className="shrink">For</th>
                      <th className="secondary">Vehicle</th>
                      <th className="secondary">Card</th>
                      {th("amount", "Amount", "num")}
                      {th("status", "Status")}
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((c) => (
                      <tr key={c.id} onClick={() => onOpen(c.id)}
                          className={c.status === "voided" ? "muted" : ""}>
                        <td className="nowrap">{shortDate(c.created_at)}</td>
                        <td className="mono">{c.charge_no}</td>
                        <td className="strong">{c.customer_name}</td>
                        <td className="shrink">{c.description ?? <span className="dim">—</span>}</td>
                        <td className="secondary">{c.vehicle ?? <span className="dim">—</span>}</td>
                        <td className="mono dim secondary">
                          {c.card_last4 ? `•••• ${c.card_last4}` : "—"}
                        </td>
                        <td className="num strong">{formatMoney(c.amount_cents)}</td>
                        <td>
                          <span className={`pill ${STATUS_CLASS[c.status] ?? "hold"}`}>
                            {STATUS_LABEL[c.status]}
                          </span>
                          {c.status === "open" && c.has_link && !c.link_expired && (
                            <span className="hint dim"> link sent</span>
                          )}
                          {c.status === "open" && c.link_expired && (
                            <span className="hint err"> link expired</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager page={page} setPage={setPage} total={shown.length} />
            </>
          )}
        </>
      )}
    </>
  );
}
